import React, { useId, useState } from 'react';
import { ChartBarIcon, LockSimpleIcon } from '@phosphor-icons/react';

import { MPFButton } from 'volto-ministerio-publico-federal/components/UI';
import type { PollOptionResult } from './types';
import type { HeadingLevel } from 'volto-ministerio-publico-federal/types/heading';

export interface PollFormProps {
  question: string;
  description?: string;
  options: PollOptionResult[];
  total?: number;
  onSubmit: (optionId: string) => Promise<void> | void;
  submitting?: boolean;
  errorMessage?: string | null;
  /**
   * Nível do heading da pergunta. O default `h3` assume a enquete como
   * subseção de um bloco que já tem h2; num contexto mais raso, passe `h2`.
   */
  as?: HeadingLevel;
}

const DEFAULT_SUBTITLE = 'Escolha uma opção para participar da pesquisa.';

function formatVotes(count: number): string {
  return count === 1 ? '1 voto até agora' : `${count} votos até agora`;
}

const PollForm: React.FC<PollFormProps> = ({
  question,
  description,
  options,
  total = 0,
  onSubmit,
  submitting = false,
  errorMessage = null,
  as: Heading = 'h3',
}) => {
  const groupId = useId();
  const [selected, setSelected] = useState<string | null>(null);
  const subtitle = (description ?? '').trim() || DEFAULT_SUBTITLE;

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selected || submitting) return;
    void onSubmit(selected);
  };

  return (
    <form
      className="poll-form"
      onSubmit={handleSubmit}
      aria-busy={submitting || undefined}
      noValidate
    >
      <header className="poll-block__header">
        <span className="poll-block__eyebrow">
          <ChartBarIcon weight="fill" aria-hidden="true" />
          Enquete
        </span>
        <Heading className="poll-block__question">{question}</Heading>
        <p className="poll-block__subtitle">{subtitle}</p>
      </header>

      <fieldset className="poll-form__fieldset" disabled={submitting}>
        <legend className="sr-only">{question}</legend>
        <ul className="poll-form__options">
          {options.map((option) => {
            const inputId = `${groupId}-${option.id}`;
            const isChecked = selected === option.id;
            return (
              <li key={option.id}>
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control -- label envolve um radio input visualmente oculto e o texto descendente; padrão acessível padrão. */}
                <label
                  htmlFor={inputId}
                  className="poll-option"
                  data-selected={isChecked ? 'true' : 'false'}
                >
                  <input
                    type="radio"
                    id={inputId}
                    name={groupId}
                    value={option.id}
                    checked={isChecked}
                    onChange={() => setSelected(option.id)}
                    className="poll-option__input"
                  />
                  <span className="poll-option__body">
                    <span className="poll-option__label">{option.label}</span>
                  </span>
                  <span className="poll-option__indicator" aria-hidden="true">
                    <span className="poll-option__indicator-dot" />
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      </fieldset>

      {errorMessage ? (
        <p className="poll-block__error" role="alert">
          {errorMessage}
        </p>
      ) : null}

      <MPFButton
        type="submit"
        variant="unstyled"
        className="poll-block__submit"
        disabled={!selected}
        loading={submitting}
      >
        {submitting ? 'Registrando voto…' : 'Confirmar voto'}
      </MPFButton>

      <footer className="poll-block__footer">
        <span className="poll-block__footer-item">
          <LockSimpleIcon aria-hidden="true" />
          Resposta anônima
        </span>
        <span className="poll-block__footer-item">{formatVotes(total)}</span>
      </footer>
    </form>
  );
};

export default PollForm;
